/**
 * Generic UI glyphs — wrap lucide-react at a heavier stroke weight,
 * standardized across the app. See drawing-tools.tsx for the hand-drawn
 * chart glyphs these are deliberately kept separate from.
 * All icons accept a `size` prop (number, pixels) and inherit currentColor.
 */
import {
  Clock, Sun, Moon, Search, Plus, PlusCircle, X, Trash2, Lock, Unlock,
  Eye, EyeOff, ChevronRight, ChevronDown, PanelLeft, Settings, Menu, Bell, Bot,
  LogOut, Download, Upload, RefreshCw, Check, Sparkles,
  Send, Star, Calendar, Maximize2, Minimize2, Tag, Magnet, FileBarChart, Share2, House,
} from 'lucide-react';
import { FacebookLogo, YoutubeLogo, LinkedinLogo } from '@phosphor-icons/react';
import type { IconProps } from './types';

// lucide's default stroke-width (2) reads thinner than Phosphor's "bold"
// weight this app was designed around — bump it slightly for the same visual weight.
const BOLD = 2.5;

export function HomeIcon({ size = 16, className }: IconProps) {
  return <House size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function ClockIcon({ size = 16, className }: IconProps) {
  return <Clock size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function SunIcon({ size = 16, className }: IconProps) {
  return <Sun size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function MoonIcon({ size = 16, className }: IconProps) {
  return <Moon size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function SearchIcon({ size = 16, className }: IconProps) {
  return <Search size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function PlusIcon({ size = 16, className }: IconProps) {
  return <Plus size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function PlusCircleIcon({ size = 16, className }: IconProps) {
  return <PlusCircle size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function XIcon({ size = 16, className }: IconProps) {
  return <X size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function TrashIcon({ size = 16, className }: IconProps) {
  return <Trash2 size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function TagIcon({ size = 16, className }: IconProps) {
  return <Tag size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function MagnetIcon({ size = 16, className }: IconProps) {
  return <Magnet size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function LockIcon({ size = 16, className }: IconProps) {
  return <Lock size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function UnlockIcon({ size = 16, className }: IconProps) {
  return <Unlock size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function EyeIcon({ size = 16, className }: IconProps) {
  return <Eye size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function EyeOffIcon({ size = 16, className }: IconProps) {
  return <EyeOff size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function ChevronRightIcon({ size = 16, className }: IconProps) {
  return <ChevronRight size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function ChevronDownIcon({ size = 16, className }: IconProps) {
  return <ChevronDown size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function PanelLeftIcon({ size = 16, className }: IconProps) {
  return <PanelLeft size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function GearIcon({ size = 16, className }: IconProps) {
  return <Settings size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function MenuIcon({ size = 16, className }: IconProps) {
  return <Menu size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function BellIcon({ size = 16, className }: IconProps) {
  return <Bell size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function FinancialsIcon({ size = 16, className }: IconProps) {
  return <FileBarChart size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function BotIcon({ size = 16, className }: IconProps) {
  return <Bot size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function CameraIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={BOLD} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

export function ShareIcon({ size = 16, className }: IconProps) {
  return <Share2 size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function ExpandIcon({ size = 16, className }: IconProps) {
  return <Maximize2 size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function CompressIcon({ size = 16, className }: IconProps) {
  return <Minimize2 size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function CheckIcon({ size = 16, className }: IconProps) {
  return <Check size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function SparklesIcon({ size = 16, className }: IconProps) {
  return <Sparkles size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function SendIcon({ size = 16, className }: IconProps) {
  return <Send size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function LogOutIcon({ size = 16, className }: IconProps) {
  return <LogOut size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function DownloadIcon({ size = 16, className }: IconProps) {
  return <Download size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function UploadIcon({ size = 16, className }: IconProps) {
  return <Upload size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function RefreshIcon({ size = 16, className }: IconProps) {
  return <RefreshCw size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function StarIcon({ size = 16, className }: IconProps) {
  return <Star size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

export function StarFilledIcon({ size = 16, className }: IconProps) {
  return <Star size={size} strokeWidth={BOLD} fill="currentColor" className={className} aria-hidden />;
}

export function CalendarIcon({ size = 16, className }: IconProps) {
  return <Calendar size={size} strokeWidth={BOLD} className={className} aria-hidden />;
}

// Brand logos — kept on Phosphor's actual logo marks (fill weight) rather
// than a lucide/inline-SVG approximation, per product decision to leave
// these three untouched.
export function FacebookIcon({ size = 16, className }: IconProps) {
  return <FacebookLogo size={size} weight="fill" className={className} aria-hidden />;
}

export function YoutubeIcon({ size = 16, className }: IconProps) {
  return <YoutubeLogo size={size} weight="fill" className={className} aria-hidden />;
}

export function LinkedinIcon({ size = 16, className }: IconProps) {
  return <LinkedinLogo size={size} weight="fill" className={className} aria-hidden />;
}
